import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  RefreshCw,
  ChevronDown,
  Database,
  Users,
  Clock,
  Zap,
} from "lucide-react";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts";
import { CenterHeader } from "./center/CenterPrimitives";

/* ---- palette (Grafana-like) ---- */
const C = {
  green: "oklch(0.78 0.18 150)",
  yellow: "oklch(0.85 0.16 90)",
  orange: "oklch(0.78 0.18 55)",
  red: "oklch(0.7 0.21 30)",
  blue: "oklch(0.72 0.16 245)",
  purple: "oklch(0.7 0.18 305)",
  cyan: "oklch(0.78 0.13 200)",
  pink: "oklch(0.78 0.18 0)",
  grid: "oklch(1 0 0 / 0.06)",
  axis: "oklch(0.7 0.02 240)",
};

type Range = "15m" | "1h" | "24h";
const POINTS: Record<Range, number> = { "15m": 30, "1h": 60, "24h": 96 };

function ts(range: Range, n: number, i: number) {
  const total = range === "15m" ? 15 * 60 : range === "1h" ? 60 * 60 : 24 * 60 * 60;
  const stepSec = total / n;
  const now = new Date();
  const d = new Date(now.getTime() - (n - 1 - i) * stepSec * 1000);
  return range === "24h"
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function series(range: Range, gen: (i: number) => Record<string, number>) {
  const n = POINTS[range];
  return Array.from({ length: n }, (_, i) => ({ t: ts(range, n, i), ...gen(i) }));
}

function rnd(seed: number, i: number, base: number, amp: number) {
  return base + Math.sin(i / 3 + seed) * amp * 0.6 + Math.cos(i / 5 + seed * 2) * amp * 0.4;
}

/* ---- shared bits ---- */
function CardShell({
  title,
  legend,
  children,
  className = "",
}: {
  title: string;
  legend?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={"card-surface rounded-2xl p-4 " + className}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-xs font-medium text-muted-foreground">{title}</div>
        {legend}
      </div>
      {children}
    </div>
  );
}

function Tip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-white/10 bg-black/80 px-2.5 py-1.5 text-[11px] shadow-xl backdrop-blur">
      <div className="text-muted-foreground">{label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-foreground">{p.name}</span>
          <span className="ml-auto font-mono tabular-nums">{Number(p.value).toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
}

function LegendChips({ items }: { items: { label: string; color: string; last?: string }[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-1.5">
          <span className="h-1.5 w-3 rounded-sm" style={{ background: it.color }} />
          {it.label}
          {it.last && <span className="text-foreground/80 ml-1">{it.last}</span>}
        </span>
      ))}
    </div>
  );
}

/* ---- KPI tiles ---- */
function KpiUptime({ value }: { value: number }) {
  const data = Array.from({ length: 20 }, (_, i) => ({ x: i, y: 30 + Math.sin(i / 2) * 12 + i * 1.5 }));
  return (
    <CardShell title="Max Uptime">
      <div className="flex items-end gap-1">
        <div className="text-4xl font-light">{value}</div>
        <div className="pb-1 text-xs text-muted-foreground">mins</div>
      </div>
      <div className="mt-2 h-16">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="upg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.blue} stopOpacity={0.5} />
                <stop offset="100%" stopColor={C.blue} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey="y" stroke={C.blue} fill="url(#upg)" strokeWidth={1.5} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </CardShell>
  );
}

function KpiClients({ value }: { value: number }) {
  return (
    <CardShell title="Clients">
      <div className="text-5xl font-light">{value}</div>
      <div className="mt-4 h-10 rounded-md bg-[oklch(0.72_0.16_245/0.25)]" />
    </CardShell>
  );
}

function KpiMemoryGauge({ pct }: { pct: number }) {
  const color = pct < 60 ? C.green : pct < 85 ? C.orange : C.red;
  const data = [{ name: "v", value: pct, fill: color }];
  return (
    <CardShell title="Memory Usage">
      <div className="relative h-32">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart innerRadius="72%" outerRadius="100%" startAngle={210} endAngle={-30} data={data}>
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar background={{ fill: "oklch(1 0 0 / 0.07)" }} dataKey="value" cornerRadius={20} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-2xl font-light" style={{ color }}>
          {pct}%
        </div>
      </div>
    </CardShell>
  );
}

/* ---- main view ---- */
export default function RedisMetricsView({ onBack }: { onBack: () => void }) {
  const [range, setRange] = useState<Range>("1h");
  const [auto, setAuto] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, [auto]);

  const cmdData = useMemo(
    () =>
      series(range, (i) => ({
        get: Math.max(0, rnd(1 + tick * 0.1, i, 0.35, 0.18)),
        set: Math.max(0, rnd(2 + tick * 0.1, i, 0.22, 0.14)),
        del: Math.max(0, rnd(3 + tick * 0.1, i, 0.12, 0.08)),
        incr: Math.max(0, rnd(4 + tick * 0.1, i, 0.1, 0.07)),
        expire: Math.max(0, rnd(5 + tick * 0.1, i, 0.08, 0.05)),
        publish: Math.max(0, rnd(6 + tick * 0.1, i, 0.05, 0.04)),
        ping: Math.max(0, rnd(7 + tick * 0.1, i, 0.04, 0.03)),
      })),
    [range, tick],
  );

  const hitsData = useMemo(
    () =>
      series(range, (i) => ({
        hits: Math.max(0, 60 + Math.sin(i / 6 + tick * 0.1) * 25),
        misses: Math.max(0, 8 + Math.cos(i / 8 + tick * 0.1) * 6),
      })),
    [range, tick],
  );

  const memData = useMemo(
    () =>
      series(range, (i) => ({
        used: 1.2 + Math.sin(i / 10) * 0.05 + tick * 0.001,
        max: 2,
      })),
    [range, tick],
  );

  const netData = useMemo(
    () =>
      series(range, (i) => ({
        in: 900 + Math.sin(i / 4 + tick * 0.1) * 280,
        out: 700 + Math.cos(i / 5 + tick * 0.1) * 220,
      })),
    [range, tick],
  );

  const itemsDb = useMemo(
    () =>
      series(range, (i) => ({
        db0: 350 + Math.sin(i / 8) * 30,
        db3: 330 + Math.cos(i / 6) * 25,
        db5: 320 + Math.sin(i / 5) * 20,
        db8: 220 + Math.cos(i / 9) * 18,
      })),
    [range],
  );

  const expiring = useMemo(
    () =>
      series(range, (i) => ({
        not: 600 + Math.sin(i / 7) * 30,
        exp: 80 + Math.cos(i / 9) * 10,
      })),
    [range],
  );

  const evicted = useMemo(
    () =>
      series(range, (i) => ({
        expired: Math.max(0, 2 + Math.sin(i / 4) * 1.5),
        evicted: Math.max(0, 1 + Math.cos(i / 6) * 1),
      })),
    [range],
  );

  const conn = useMemo(
    () =>
      series(range, (i) => ({
        connected: 1 + Math.round(Math.abs(Math.sin(i / 12))),
        blocked: 0,
      })),
    [range],
  );

  const avgCmd = useMemo(
    () =>
      series(range, (i) => ({
        get: 220 + Math.sin(i / 2 + tick * 0.2) * 60 + (i % 11 === 0 ? 120 : 0),
        set: 250 + Math.cos(i / 3 + tick * 0.2) * 50,
        del: 180 + Math.sin(i / 4) * 40,
        publish: 40 + Math.cos(i / 5) * 15,
      })),
    [range, tick],
  );

  const totalCmd = useMemo(
    () =>
      series(range, (i) => ({
        get: 40 + Math.sin(i / 3 + tick * 0.1) * 12,
        set: 30 + Math.cos(i / 4 + tick * 0.1) * 10,
        del: 18 + Math.sin(i / 5) * 6,
        ping: 8 + Math.cos(i / 6) * 3,
      })),
    [range, tick],
  );

  const cmdLegend = [
    { label: "GET", color: C.green },
    { label: "SET", color: C.blue },
    { label: "DEL", color: C.red },
    { label: "INCR", color: C.orange },
    { label: "EXPIRE", color: C.purple },
    { label: "PUBLISH", color: C.cyan },
    { label: "PING", color: C.yellow },
  ];

  return (
    <div className="flex flex-col gap-4">
      <CenterHeader title="Redis Metrics" scope="Cache · pub/sub · clients · mémoire — temps réel" onBack={onBack} />

      {/* Toolbar */}
      <div className="card-surface flex flex-wrap items-center gap-2 rounded-2xl p-2">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs text-muted-foreground">
          namespace <ChevronDown className="h-3 w-3" />
        </div>
        <div className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs">
          instance <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px]">redis</span>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {(["15m", "1h", "24h"] as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={
                "rounded-full px-3 py-1.5 text-xs " +
                (range === r ? "bg-foreground text-background" : "bg-white/5 text-muted-foreground hover:text-foreground")
              }
            >
              Last {r}
            </button>
          ))}
          <button
            onClick={() => setAuto((a) => !a)}
            className={
              "ml-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs " +
              (auto ? "bg-success/15 text-success" : "bg-white/5 text-muted-foreground")
            }
          >
            <Activity className="h-3 w-3" /> {auto ? "Live" : "Paused"}
          </button>
          <button
            onClick={() => setTick((t) => t + 1)}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Top row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <KpiUptime value={56 + (tick % 60)} />
        <KpiClients value={1 + (tick % 3)} />
        <KpiMemoryGauge pct={5 + (tick % 7)} />

        <CardShell title="Total Commands / sec" className="lg:col-span-1" legend={<LegendChips items={cmdLegend} />}>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cmdData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={32} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" stackId="1" dataKey="get" stroke={C.green} fill={C.green} fillOpacity={0.75} name="GET" />
                <Area type="monotone" stackId="1" dataKey="set" stroke={C.blue} fill={C.blue} fillOpacity={0.75} name="SET" />
                <Area type="monotone" stackId="1" dataKey="del" stroke={C.red} fill={C.red} fillOpacity={0.7} name="DEL" />
                <Area type="monotone" stackId="1" dataKey="incr" stroke={C.orange} fill={C.orange} fillOpacity={0.7} name="INCR" />
                <Area type="monotone" stackId="1" dataKey="expire" stroke={C.purple} fill={C.purple} fillOpacity={0.7} name="EXPIRE" />
                <Area type="monotone" stackId="1" dataKey="publish" stroke={C.cyan} fill={C.cyan} fillOpacity={0.7} name="PUBLISH" />
                <Area type="monotone" stackId="1" dataKey="ping" stroke={C.yellow} fill={C.yellow} fillOpacity={0.7} name="PING" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardShell>

        <CardShell title="Hits / Misses per Sec" legend={<LegendChips items={[{ label: "hits", color: C.green }, { label: "misses", color: C.red }]} />}>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={hitsData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={32} domain={[0, 100]} />
                <Tooltip content={<Tip />} />
                <Line type="monotone" dataKey="hits" stroke={C.green} strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="misses" stroke={C.red} strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardShell>
      </div>

      {/* Memory + Network */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <CardShell title="Total Memory Usage" legend={<LegendChips items={[{ label: "used, redis", color: C.green }, { label: "max, redis", color: C.yellow }]} />}>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={memData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="memG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.yellow} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={C.yellow} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={48} tickFormatter={(v) => `${v.toFixed(2)} GiB`} domain={[0, 2.2]} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" dataKey="max" stroke={C.yellow} fill="url(#memG)" strokeWidth={1.5} name="max" />
                <Area type="monotone" dataKey="used" stroke={C.green} fill={C.green} fillOpacity={0.25} strokeWidth={1.5} name="used" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardShell>

        <CardShell title="Network I/O" legend={<LegendChips items={[{ label: "in", color: C.green }, { label: "out", color: C.yellow }]} />}>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={netData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={48} tickFormatter={(v) => `${(v / 1024).toFixed(2)} KiB`} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" dataKey="in" stroke={C.green} fill={C.green} fillOpacity={0.3} strokeWidth={1.5} name="in" />
                <Area type="monotone" dataKey="out" stroke={C.yellow} fill={C.yellow} fillOpacity={0.3} strokeWidth={1.5} name="out" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardShell>
      </div>

      {/* Items / Expiring */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <CardShell
          title="Total Items per DB"
          legend={
            <LegendChips
              items={[
                { label: "db0", color: C.green, last: "Last *: 7" },
                { label: "db3", color: C.yellow, last: "Last *: 337" },
                { label: "db5", color: C.orange, last: "Last *: 2" },
                { label: "db8", color: C.purple, last: "Last *: 224" },
              ]}
            />
          }
        >
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={itemsDb} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={36} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" stackId="1" dataKey="db0" stroke={C.green} fill={C.green} fillOpacity={0.7} />
                <Area type="monotone" stackId="1" dataKey="db3" stroke={C.yellow} fill={C.yellow} fillOpacity={0.7} />
                <Area type="monotone" stackId="1" dataKey="db5" stroke={C.orange} fill={C.orange} fillOpacity={0.7} />
                <Area type="monotone" stackId="1" dataKey="db8" stroke={C.purple} fill={C.purple} fillOpacity={0.7} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardShell>

        <CardShell
          title="Expiring vs Not-Expiring Keys"
          legend={<LegendChips items={[{ label: "not expiring, redis", color: C.green }, { label: "expiring, redis", color: C.yellow }]} />}
        >
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={expiring} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={36} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" dataKey="not" stroke={C.green} fill={C.green} fillOpacity={0.55} name="not expiring" />
                <Area type="monotone" dataKey="exp" stroke={C.yellow} fill={C.yellow} fillOpacity={0.55} name="expiring" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardShell>
      </div>

      {/* Expired / Connected */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <CardShell title="Expired/Evicted Keys" legend={<LegendChips items={[{ label: "expired, redis", color: C.green }, { label: "evicted, redis", color: C.yellow }]} />}>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={evicted} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={32} domain={[0, 100]} />
                <Tooltip content={<Tip />} />
                <Line type="monotone" dataKey="expired" stroke={C.green} strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="evicted" stroke={C.yellow} strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardShell>

        <CardShell title="Connected/Blocked Clients" legend={<LegendChips items={[{ label: "connected", color: C.green }, { label: "blocked", color: C.yellow }]} />}>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={conn} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={32} domain={[0, "dataMax + 1"]} />
                <Tooltip content={<Tip />} />
                <Line type="monotone" dataKey="connected" stroke={C.green} strokeWidth={1.5} dot={{ r: 2, fill: C.green }} />
                <Line type="monotone" dataKey="blocked" stroke={C.yellow} strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardShell>
      </div>

      {/* Time spent by command */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <CardShell title="Average Time Spent by Command / sec" legend={<LegendChips items={cmdLegend.slice(0, 4)} />}>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={avgCmd} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={42} tickFormatter={(v) => `${v} µs`} />
                <Tooltip content={<Tip />} />
                <Line type="monotone" dataKey="get" stroke={C.green} strokeWidth={1.2} dot={false} />
                <Line type="monotone" dataKey="set" stroke={C.blue} strokeWidth={1.2} dot={false} />
                <Line type="monotone" dataKey="del" stroke={C.red} strokeWidth={1.2} dot={false} />
                <Line type="monotone" dataKey="publish" stroke={C.orange} strokeWidth={1.2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardShell>

        <CardShell title="Total Time Spent by Command / sec" legend={<LegendChips items={cmdLegend.slice(0, 4)} />}>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={totalCmd} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="t" tick={{ fontSize: 9, fill: C.axis }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: C.axis }} width={42} tickFormatter={(v) => `${v} µs`} />
                <Tooltip content={<Tip />} />
                <Area type="monotone" stackId="1" dataKey="get" stroke={C.green} fill={C.green} fillOpacity={0.6} />
                <Area type="monotone" stackId="1" dataKey="set" stroke={C.blue} fill={C.blue} fillOpacity={0.6} />
                <Area type="monotone" stackId="1" dataKey="del" stroke={C.red} fill={C.red} fillOpacity={0.6} />
                <Area type="monotone" stackId="1" dataKey="ping" stroke={C.orange} fill={C.orange} fillOpacity={0.6} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardShell>
      </div>

      {/* Footer stats */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { icon: Database, label: "Keys totales", value: "1 247" },
          { icon: Users, label: "Pub/Sub channels", value: "12" },
          { icon: Clock, label: "Last save", value: "2m ago" },
          { icon: Zap, label: "Ops/sec peak", value: "1.4k" },
        ].map((s) => (
          <div key={s.label} className="card-surface flex items-center gap-3 rounded-2xl p-4">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-white/5 text-muted-foreground">
              <s.icon className="h-4 w-4" strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-lg font-light">{s.value}</div>
              <div className="text-[11px] text-muted-foreground">{s.label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
